import { Belonging } from '@spike/belonging-model';
import { BelongingsStatus, createBelongingThunk } from '@spike/belongings-action';
import useNonInitialEffect from '@versiondos/hooks';
import GenericModal from 'components/UI/V3/Modals/GenericModal';
import Button from 'components/UI/V3/Button/Button';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import { useApiClientWrapper } from 'hooks';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import styles from './AddBelongingModal.module.scss';

interface AddBelongingModalProps {
    onSaved: (belonging: Belonging) => void;
    onDiscard: () => void;
}

const AddBelongingModal: React.FC<AddBelongingModalProps> = ({ onSaved, onDiscard }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const status = useSelector<RootState, BelongingsStatus>(state => state.belongings.status);
    const lastCreated = useSelector<RootState, Belonging | undefined>(state => state.belongings.belonging);
    const loading = useSelector<RootState, boolean>(state => state.belongings.loading);
    const validationErrors = useSelector<RootState, { name: string[] } | undefined>(
        state => state.belongings.validationErrors
    );
    const [name, setName] = useState('');
    const [duplicateError, setDuplicateError] = useState('');

    useNonInitialEffect(() => {
        if (status === BelongingsStatus.CreateSuccess && lastCreated) {
            onSaved(lastCreated);
        } else if (status === BelongingsStatus.ValidationError && validationErrors?.name?.length) {
            setDuplicateError(validationErrors.name[0]);
        }
    }, [status]);

    const handleSave = () => {
        const trimmed = name.trim();
        if (trimmed) {
            dispatch(createBelongingThunk(apiClientWrapper, trimmed));
        }
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
        if (duplicateError) setDuplicateError('');
    };

    return (
        <GenericModal
            onClose={onDiscard}
            className={styles.modal}
            footer={
                <div className={styles.footer}>
                    <Button
                        label="Discard"
                        size="large"
                        variant="black"
                        className={styles.button}
                        onClick={onDiscard}
                        disabled={loading}
                    />
                    <Button
                        label="Save"
                        size="large"
                        variant="green"
                        className={styles.button}
                        onClick={handleSave}
                        disabled={!name.trim() || loading}
                        isLoading={loading}
                    />
                </div>
            }
        >
            <div className={styles.content}>
                <h2 className={styles.title}>Add New Belonging</h2>
                <Input
                    label="Belonging Name"
                    placeholder="Ex. Collar"
                    value={name}
                    onChange={handleNameChange}
                    onEnterPressed={handleSave}
                    disabled={loading}
                    autoFocus
                />
                {duplicateError && <p className={styles.error}>{duplicateError}</p>}
            </div>
        </GenericModal>
    );
};

export default AddBelongingModal;
